import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Upload, X, ClipboardList, ChevronRight, Maximize2 } from 'lucide-react';

const ROOF_TILE_OPTIONS = [
  'Small Plain Tile', 'Large Plain Tile', 'Pan Tile', 'Slate', 'Flat', 'Metal',
];

const CABLE_ROUTE_OPTIONS = [
  'Internal (Via Loft Cavity)',
  'External (Via Conduit)',
];

const ROOF_CONDITION_OPTIONS = [
  'Good (No concern of installation)',
  'Bad (Slight concern on age of tiles or mounting)',
];

const YES_NO = ['Yes', 'No'];

const EMPTY = {
  customer_id: '',
  surveyor_name: '',
  survey_date: new Date().toISOString().slice(0, 10),
  project_address: '',
  distribution_board_location: '',
  distance_from_inverter: '',
  additional_db_required: 'No',
  current_usage_kwh: '',
  cost_per_kwh: '',
  future_changes: '',
  inverter_battery_location: '',
  cable_distance: '',
  cable_route: 'Internal (Via Loft Cavity)',
  roof_tile_type: 'Pan Tile',
  hook_stops_required: 'No',
  num_roofs: '1',
  roof_condition: 'Good (No concern of installation)',
  roof_obstructions: '',
  shading_description: '',
  notes: '',
};

const PHOTO_QUESTIONS = [
  { key: 'front_door', label: 'Picture of Front Door (approx. 10m away)' },
  { key: 'cut_off', label: 'Picture of Cut-Off (Electric Cupboard/Meter)' },
  { key: 'distribution_board', label: 'Picture of Distribution Board (space & type)' },
  { key: 'inverter_location', label: 'Image of desired inverter and battery location' },
  { key: 'roof_tile', label: 'Image of Roof Tile' },
  { key: 'shading', label: 'Pictures of potential shading' },
];

export const SurveyForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState({}); // { key: [{id, filename, stored_filename}] }
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { src, filename }

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  React.useEffect(() => {
    api.get('/customers?limit=500').then((r) => {
      const arr = Array.isArray(r.data) ? r.data : (r.data.customers || []);
      setCustomerOptions(arr.slice(0, 500));
    }).catch(() => {});
  }, []);

  const handleUpload = async (key, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(key);
    const uploaded = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await api.post('/surveys/upload-photo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(r.data);
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setPhotos((prev) => ({ ...prev, [key]: [...(prev[key] || []), ...uploaded] }));
    setUploading(null);
  };

  const removePhoto = (key, id) => {
    setPhotos((prev) => ({ ...prev, [key]: (prev[key] || []).filter((p) => p.id !== id) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.surveyor_name || !form.project_address) {
      toast.error('Your name and project address are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, photos };
      if (!payload.customer_id) delete payload.customer_id;
      const r = await api.post('/surveys', payload);
      toast.success(r.data.customer_id ? 'Survey submitted & linked to customer' : 'Survey submitted — needs customer linking');
      navigate('/surveys');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const photoBlock = (key, label) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-3">
        {(photos[key] || []).map((p) => {
          const src = `${process.env.REACT_APP_BACKEND_URL}/api/surveys/photo/${p.stored_filename}`;
          return (
            <div key={p.id} className="relative group w-32" data-testid={`photo-${key}-${p.id}`}>
              <button
                type="button"
                onClick={() => setLightbox({ src, filename: p.filename })}
                className="block w-32 h-32 rounded border border-slate-200 overflow-hidden bg-slate-100 hover:border-blue-400 transition"
                title="Click to enlarge"
              >
                <img src={src} alt={p.filename} className="w-full h-full object-cover" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate rounded-b">
                {p.filename}
              </div>
              <button
                type="button"
                onClick={() => removePhoto(key, p.id)}
                className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow"
                title="Remove"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="absolute top-1 right-1 bg-white/80 rounded p-0.5 opacity-0 group-hover:opacity-100 transition">
                <Maximize2 className="w-3 h-3 text-slate-700" />
              </div>
            </div>
          );
        })}
        <label className="w-32 h-32 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 hover:border-blue-400 text-slate-400 transition">
          {uploading === key ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Upload className="w-5 h-5" />
              <span className="text-[10px] mt-1">Add photo</span>
              <span className="text-[9px] text-slate-300 mt-0.5">tap to open camera</span>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(key, e.target.files)}
            data-testid={`upload-${key}`}
          />
        </label>
      </div>
    </div>
  );

  return (
    <Layout>
      <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="survey-form">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
              <ClipboardList className="inline w-7 h-7 mr-2 text-blue-600" />
              Solar PV & EESS Survey Form
            </h1>
            <p className="text-slate-500 mt-1">Site survey submitted by engineers on location</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/surveys')}
            data-testid="view-surveys-btn"
          >
            View Surveys <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Surveyor & Site</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Your Name *</Label>
                <Input value={form.surveyor_name} onChange={(e) => set('surveyor_name', e.target.value)} data-testid="surveyor-name-input" required />
              </div>
              <div>
                <Label>Survey Date *</Label>
                <Input type="date" value={form.survey_date} onChange={(e) => set('survey_date', e.target.value)} data-testid="survey-date-input" required />
              </div>
            </div>
            <div>
              <Label>Project Name / Address *</Label>
              <Textarea rows={2} value={form.project_address} onChange={(e) => set('project_address', e.target.value)} placeholder="e.g. 42 Oak Lane, Canterbury, CT1 2AB" data-testid="project-address-input" required />
            </div>
            <div>
              <Label>Link to customer (optional — auto-match is attempted by postcode if blank)</Label>
              <Select value={form.customer_id || 'none'} onValueChange={(v) => set('customer_id', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder="Auto-match by postcode" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="none">— Auto-match by postcode —</SelectItem>
                  {customerOptions.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.postcode})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {photoBlock('front_door', '3. Picture of Front Door (approx. 10m away) *')}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Electrical</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {photoBlock('cut_off', '4. Picture of Cut-Off (Electric Cupboard/Meter) *')}
            <div>
              <Label>5. Distribution Board Location *</Label>
              <Textarea rows={2} value={form.distribution_board_location} onChange={(e) => set('distribution_board_location', e.target.value)} data-testid="db-location-input" />
            </div>
            {photoBlock('distribution_board', '6. Picture of Distribution Board (available space + board type) *')}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>7. Distance from Inverter (meters) *</Label>
                <Input value={form.distance_from_inverter} onChange={(e) => set('distance_from_inverter', e.target.value)} data-testid="distance-inverter-input" />
              </div>
              <div>
                <Label>8. Is an additional DB required? *</Label>
                <Select value={form.additional_db_required} onValueChange={(v) => set('additional_db_required', v)}>
                  <SelectTrigger data-testid="additional-db-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YES_NO.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Electricity Bill</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>9. Current Usage (kWh — ideally annual) *</Label>
              <Textarea rows={2} value={form.current_usage_kwh} onChange={(e) => set('current_usage_kwh', e.target.value)} data-testid="usage-input" />
            </div>
            <div>
              <Label>10. Cost of electricity (per kWh) — ask customer to email bill over *</Label>
              <Textarea rows={2} value={form.cost_per_kwh} onChange={(e) => set('cost_per_kwh', e.target.value)} data-testid="cost-input" />
            </div>
            <div>
              <Label>11. Any future changes (EV charger, AC install, etc.) *</Label>
              <Textarea rows={2} value={form.future_changes} onChange={(e) => set('future_changes', e.target.value)} data-testid="future-input" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Inverter & Battery</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>12. Desired location for Inverter & Battery *</Label>
              <Textarea rows={2} value={form.inverter_battery_location} onChange={(e) => set('inverter_battery_location', e.target.value)} data-testid="inverter-loc-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>13. Cable distance from panels to inverter (m) *</Label>
                <Input value={form.cable_distance} onChange={(e) => set('cable_distance', e.target.value)} data-testid="cable-distance-input" />
              </div>
              <div>
                <Label>14. Route of cable installation *</Label>
                <Select value={form.cable_route} onValueChange={(v) => set('cable_route', v)}>
                  <SelectTrigger data-testid="cable-route-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CABLE_ROUTE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {photoBlock('inverter_location', '15. Image of desired inverter and battery location *')}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Roof</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>16. Type of Roof Tile *</Label>
                <Select value={form.roof_tile_type} onValueChange={(v) => set('roof_tile_type', v)}>
                  <SelectTrigger data-testid="roof-tile-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROOF_TILE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>17. Are Hook Stops required? *</Label>
                <Select value={form.hook_stops_required} onValueChange={(v) => set('hook_stops_required', v)}>
                  <SelectTrigger data-testid="hook-stops-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YES_NO.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {photoBlock('roof_tile', '18. Image of Roof Tile *')}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>19. How many roofs being installed on? *</Label>
                <Input value={form.num_roofs} onChange={(e) => set('num_roofs', e.target.value)} data-testid="num-roofs-input" />
              </div>
              <div>
                <Label>20. Roof Condition *</Label>
                <Select value={form.roof_condition} onValueChange={(v) => set('roof_condition', v)}>
                  <SelectTrigger data-testid="roof-condition-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROOF_CONDITION_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>21. Any obstructions to roof (vent, Velux, etc.) *</Label>
              <Textarea rows={2} value={form.roof_obstructions} onChange={(e) => set('roof_obstructions', e.target.value)} data-testid="obstructions-input" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Shading & Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>22. Potential shading within 10m (trees, chimneys, etc.) *</Label>
              <Textarea rows={2} value={form.shading_description} onChange={(e) => set('shading_description', e.target.value)} data-testid="shading-desc-input" />
            </div>
            {photoBlock('shading', '23. Pictures of potential shading *')}
            <div>
              <Label>24. Any other notes or comments</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="notes-input" />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => setForm(EMPTY)}>Reset</Button>
          <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-survey-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Submit Survey
          </Button>
        </div>
      </form>

      {/* Photo lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-5xl p-2 bg-black/95 border-0" data-testid="photo-lightbox">
          {lightbox && (
            <div className="flex flex-col items-center">
              <img
                src={lightbox.src}
                alt={lightbox.filename}
                className="max-h-[80vh] w-auto object-contain rounded"
              />
              <div className="flex items-center justify-between w-full mt-2 px-2 text-white">
                <span className="text-sm truncate">{lightbox.filename}</span>
                <a
                  href={lightbox.src}
                  target="_blank"
                  rel="noreferrer"
                  download={lightbox.filename}
                  className="text-xs px-3 py-1 bg-white/10 hover:bg-white/20 rounded transition"
                >
                  Open / Download original
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SurveyForm;
